/**
 * useNoteLifecycleController — Phase 3B-2 step 1 (Jun 2026)
 * ─────────────────────────────────────────────────────────
 * The note close / discard / save-and-close lifecycle handlers, extracted
 * VERBATIM from the legacy Dashboard (App.js) so the upcoming V2-native
 * note host can reuse the exact same behavior (text backups, flush save,
 * unsaved-changes guards, Next-Steps handoff, return-path navigation).
 *
 * Zero behavior change: bodies are byte-identical to the originals; the
 * Dashboard passes its own state/setters through the `deps` bag.
 */
import { useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { extractNoteTextContent } from '../utils/noteTextExtractor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Phase 3B-1 (Jun 2026): V2 pages open notes at /note/:id?return=/v2/...
// so closing the editor lands back on the exact V2 page that opened it
// (instead of stranding the user on the legacy dashboard).  Only /v2
// paths are honored to avoid open-redirect misuse.
export const getV2NoteReturnPath = () => {
  try {
    const ret = new URLSearchParams(window.location.search).get('return');
    return ret && ret.startsWith('/v2') ? ret : null;
  } catch {
    return null;
  }
};

export const useNoteLifecycleController = (deps) => {
  const {
    isEditingNote, selectedNote, editedNoteContent, noteJustSigned, showNoteDetail,
    showClientDetail, selectedClient, clientDetailTab,
    noteEditingAutosave, navigate, refreshClientNotes,
    setIsEditingNote, setEditedNoteContent, setEditedNoteConsent, setLinkedGoals,
    setSelectedNote, setShowNoteDetail, setShowCloseEditingNoteWarning,
    setShowNextStepsDialog, setNoteJustSigned,
  } = deps;

  const closeNoteWithCheck = useCallback(async () => {
    void 0 && console.log('[closeNoteWithCheck] CALLED - Starting close process');
    void 0 && console.log('[closeNoteWithCheck] selectedNote:', selectedNote?.id, 'editedNoteContent exists:', !!editedNoteContent);
    
    const saveStatus = noteEditingAutosave?.saveStatus;
    const isSaving = saveStatus === 'saving';
    
    // CRITICAL FIX: Use hasRealUnsavedChanges() for content comparison instead of hasUnsavedChanges flag
    // This ensures we only show the warning when there are MEANINGFUL differences from server
    // The function compares current form data against the saved baseline using deep equality
    const hasRealChanges = noteEditingAutosave?.hasRealUnsavedChanges?.() ?? false;
    
    void 0 && console.log('[closeNoteWithCheck] Exit guard check:', {
      saveStatus,
      isSaving,
      hasRealChanges
    });
    
    // CRITICAL: Check for ERROR or OFFLINE_CACHED states - data NOT synced to server!
    if (saveStatus === 'error' || saveStatus === 'offline_cached') {
      const shouldClose = window.confirm(
        '⚠️ WARNING: Your changes are NOT saved on the server!\n\n' +
        'Your internet connection may be lost or there was a server error.\n\n' +
        'If you close this note now, YOUR WORK WILL BE LOST.\n\n' +
        'Please copy your work before closing, or click Cancel and try the Retry button.'
      );
      if (!shouldClose) {
        return false; // Don't close
      }
    } else if (isEditingNote && (hasRealChanges || isSaving)) {
      // Show the custom modal with 3 options
      // Also show if save is in progress - let user decide to wait or close
      // Use setTimeout to break out of any synchronous event handling (like Radix Dialog's onOpenChange)
      setTimeout(() => {
        setShowCloseEditingNoteWarning(true);
      }, 0);
      return false; // Don't close yet - wait for modal response
    }
    
    // FLUSH SAVE: Before closing, trigger a final save to capture any last-second typing
    // This is critical because beforeunload doesn't fire on internal navigation
    // The flushSave function saves immediately without debounce
    if (noteEditingAutosave?.flushSave) {
      void 0 && console.log('[closeNoteWithCheck] Flushing any pending saves before close...');
      try {
        await noteEditingAutosave.flushSave();
      } catch (e) {
        console.warn('[closeNoteWithCheck] Flush save failed:', e);
      }
    }
    
    // SAVE TEXT BACKUP: Create a human-readable text snapshot for recovery
    void 0 && console.log('[TextBackup:CloseWithCheck] selectedNote:', selectedNote?.id, 'editedNoteContent:', !!editedNoteContent);
    if (selectedNote?.id && editedNoteContent) {
      try {
        const textContent = extractNoteTextContent(editedNoteContent, selectedNote);
        void 0 && console.log('[TextBackup:CloseWithCheck] Extracted text length:', textContent?.length);
        if (textContent && textContent.trim().length > 0) {
          void 0 && console.log('[TextBackup:CloseWithCheck] Sending backup for note:', selectedNote.id);
          axios.post(`${API}/notes/${selectedNote.id}/text-backup`, {
            text_content: textContent,
            client_id: selectedNote.client_id,
            client_name: selectedNote.client_name,
            template_name: selectedNote.template_name || selectedNote.form_name
          }, {
            headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
          }).then(res => {
            void 0 && console.log('[TextBackup:CloseWithCheck] Backup saved successfully:', res.data);
          }).catch(err => {
            console.warn('[TextBackup:CloseWithCheck] Failed to save text backup:', err);
          });
        }
      } catch (e) {
        console.warn('[TextBackup:CloseWithCheck] Error creating text backup:', e);
      }
    }
    
    // CRITICAL: Use functional updates to ensure proper state queue processing
    // This prevents React Error #300 during rapid interactions
    setIsEditingNote(() => false);
    
    // Check if note was just signed - show Next Steps dialog
    const showingNextSteps = noteJustSigned;
    if (noteJustSigned) {
      setShowNextStepsDialog(true);
      setNoteJustSigned(false);
    }
    
    // Now safe to clear note state
    // IMPORTANT: Don't clear selectedNote if the Next Steps dialog is about to open —
    // the dialog's click handlers rely on selectedNote to update the note status.
    setTimeout(() => {
      setEditedNoteContent(() => null);
      setEditedNoteConsent(() => null);
      setLinkedGoals(() => []);
      if (!showingNextSteps) {
        setSelectedNote(() => null);
      }
    }, 100);
    
    setShowNoteDetail(() => false);
    
    // Refresh client notes to ensure the document list is up-to-date
    // after any changes made inside the viewer (status, lock, sign, etc.)
    if (refreshClientNotes) refreshClientNotes();
    
    // Update URL to remove noteId but preserve tab
    if (showClientDetail && selectedClient?.id) {
      const urlParams = new URLSearchParams(window.location.search);
      const currentTab = urlParams.get('tab') || clientDetailTab;
      const tabParam = currentTab && currentTab !== 'overview' ? `?tab=${currentTab}` : '';
      navigate(`/client/${selectedClient.id}${tabParam}`, { replace: true });
    } else {
      navigate(getV2NoteReturnPath() || '/dashboard', { replace: true });
    }
    
    return true;
  }, [isEditingNote, noteEditingAutosave, noteJustSigned, showNoteDetail, selectedNote, editedNoteContent, navigate, showClientDetail, selectedClient, clientDetailTab, refreshClientNotes]);
  
  // Handlers for the close editing note warning modal
  const handleCloseEditingNoteKeepEditing = useCallback(() => {
    setShowCloseEditingNoteWarning(false);
  }, []);
  
  const handleCloseEditingNoteDiscard = useCallback(() => {
    void 0 && console.log('[handleCloseEditingNoteDiscard] CALLED - User chose to discard');
    void 0 && console.log('[handleCloseEditingNoteDiscard] selectedNote:', selectedNote?.id, 'editedNoteContent exists:', !!editedNoteContent);
    
    setShowCloseEditingNoteWarning(false);
    
    // CRITICAL: Save text backup BEFORE clearing state!
    // Even if user discards, we want to capture what they typed
    void 0 && console.log('[TextBackup:Discard] selectedNote:', selectedNote?.id, 'editedNoteContent:', !!editedNoteContent);
    if (selectedNote?.id && editedNoteContent) {
      try {
        const textContent = extractNoteTextContent(editedNoteContent, selectedNote);
        void 0 && console.log('[TextBackup:Discard] Extracted text length:', textContent?.length);
        if (textContent && textContent.trim().length > 0) {
          void 0 && console.log('[TextBackup:Discard] Sending backup for note:', selectedNote.id);
          axios.post(`${API}/notes/${selectedNote.id}/text-backup`, {
            text_content: textContent,
            client_id: selectedNote.client_id,
            client_name: selectedNote.client_name,
            template_name: selectedNote.template_name || selectedNote.form_name
          }, {
            headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
          }).then(res => {
            void 0 && console.log('[TextBackup:Discard] Backup saved successfully:', res.data);
          }).catch(err => {
            console.warn('[TextBackup:Discard] Failed to save text backup:', err);
          });
        }
      } catch (e) {
        console.warn('[TextBackup:Discard] Error creating text backup:', e);
      }
    }
    
    setIsEditingNote(() => false);
    setTimeout(() => {
      setEditedNoteContent(() => null);
      setEditedNoteConsent(() => null);
      setLinkedGoals(() => []);
      setSelectedNote(() => null);
    }, 100);
    setShowNoteDetail(() => false);
    
    // Refresh client notes after discard to ensure document list is current
    if (refreshClientNotes) refreshClientNotes();
    
    // Update URL to remove noteId but preserve tab
    if (showClientDetail && selectedClient?.id) {
      const urlParams = new URLSearchParams(window.location.search);
      const currentTab = urlParams.get('tab') || clientDetailTab;
      const tabParam = currentTab && currentTab !== 'overview' ? `?tab=${currentTab}` : '';
      navigate(`/client/${selectedClient.id}${tabParam}`, { replace: true });
    } else {
      navigate(getV2NoteReturnPath() || '/dashboard', { replace: true });
    }
  }, [navigate, showClientDetail, selectedClient, clientDetailTab, selectedNote, editedNoteContent, refreshClientNotes]);
  
  const handleCloseEditingNoteSaveAndClose = useCallback(async () => {
    void 0 && console.log('[handleCloseEditingNoteSaveAndClose] CALLED - User chose to save and close');
    void 0 && console.log('[handleCloseEditingNoteSaveAndClose] selectedNote:', selectedNote?.id, 'editedNoteContent exists:', !!editedNoteContent);
    
    setShowCloseEditingNoteWarning(false);
    
    // CRITICAL: Save text backup FIRST, before any state clearing
    // This ensures we capture the content even if forceSave hangs
    void 0 && console.log('[TextBackup:SaveAndClose] SAVING BACKUP NOW');
    if (selectedNote?.id && editedNoteContent) {
      try {
        const textContent = extractNoteTextContent(editedNoteContent, selectedNote);
        void 0 && console.log('[TextBackup:SaveAndClose] Extracted text length:', textContent?.length);
        if (textContent && textContent.trim().length > 0) {
          void 0 && console.log('[TextBackup:SaveAndClose] Sending backup for note:', selectedNote.id);
          axios.post(`${API}/notes/${selectedNote.id}/text-backup`, {
            text_content: textContent,
            client_id: selectedNote.client_id,
            client_name: selectedNote.client_name,
            template_name: selectedNote.template_name || selectedNote.form_name
          }, {
            headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
          }).then(res => {
            void 0 && console.log('[TextBackup:SaveAndClose] Backup saved successfully:', res.data);
          }).catch(err => {
            console.warn('[TextBackup:SaveAndClose] Failed to save text backup:', err);
          });
        } else {
          void 0 && console.log('[TextBackup:SaveAndClose] Text content empty, skipping');
        }
      } catch (e) {
        console.warn('[TextBackup:SaveAndClose] Error creating text backup:', e);
      }
    } else {
      void 0 && console.log('[TextBackup:SaveAndClose] Skipping - no selectedNote or editedNoteContent');
    }
    
    // Now try to save the note
    void 0 && console.log('[handleCloseEditingNoteSaveAndClose] Starting forceSave...');
    try {
      // Use the hook's forceSave - it handles version tracking properly
      const result = await noteEditingAutosave?.forceSave?.();
      void 0 && console.log('[handleCloseEditingNoteSaveAndClose] forceSave completed, result:', result);
      if (result) {
        toast.success('Changes saved');
      } else {
        console.warn('Save may not have completed');
      }
    } catch (e) {
      console.warn('Final save failed:', e);
    }
    
    setIsEditingNote(() => false);
    setTimeout(() => {
      setEditedNoteContent(() => null);
      setEditedNoteConsent(() => null);
      setLinkedGoals(() => []);
      setSelectedNote(() => null);
    }, 100);
    setShowNoteDetail(() => false);
    
    // Refresh client notes after save-and-close to ensure document list is current
    if (refreshClientNotes) refreshClientNotes();
    
    // Update URL to remove noteId but preserve tab
    if (showClientDetail && selectedClient?.id) {
      const urlParams = new URLSearchParams(window.location.search);
      const currentTab = urlParams.get('tab') || clientDetailTab;
      const tabParam = currentTab && currentTab !== 'overview' ? `?tab=${currentTab}` : '';
      navigate(`/client/${selectedClient.id}${tabParam}`, { replace: true });
    } else {
      navigate(getV2NoteReturnPath() || '/dashboard', { replace: true });
    }
  }, [noteEditingAutosave, navigate, showClientDetail, selectedClient, clientDetailTab, selectedNote, editedNoteContent, refreshClientNotes]);

  return {
    closeNoteWithCheck,
    handleCloseEditingNoteKeepEditing,
    handleCloseEditingNoteDiscard,
    handleCloseEditingNoteSaveAndClose,
  };
};
